/**
 * ModuleRouter - State-driven module switcher with lazy loading.
 *
 * Loads each module as a separate chunk via React.lazy() for code splitting.
 * Applies 250ms fade + slide transitions between modules, respecting
 * the prefers-reduced-motion media query (falls back to opacity-only).
 * Restores the last active module on launch via useUIStore.
 *
 * Requirements: 1.2, 1.4, 13.2, 14.6, 15.4
 */

import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useJournalLockStore } from '@/stores/journalLockStore';
import { JournalLockGate } from '@/components/journal/JournalLockGate';
import type { ModuleId } from '@/types/modules';

// ─── Lazy-loaded module page components (separate chunks) ────────────────────

const JournalModule = React.lazy(() => import('@/pages/JournalModule'));
const TasksModule = React.lazy(() => import('@/pages/TasksModule'));
const CalendarModule = React.lazy(() => import('@/pages/CalendarModule'));
const HabitsModule = React.lazy(() => import('@/pages/HabitsModule'));
const FocusModule = React.lazy(() => import('@/pages/FocusModule'));
const CountdownModule = React.lazy(() => import('@/pages/CountdownModule'));
const NotesModule = React.lazy(() => import('@/pages/NotesModule'));

// ─── Module component map ────────────────────────────────────────────────────

const MODULE_COMPONENTS: Record<ModuleId, React.LazyExoticComponent<React.ComponentType>> = {
  journal: JournalModule,
  tasks: TasksModule,
  calendar: CalendarModule,
  habits: HabitsModule,
  focus: FocusModule,
  countdown: CountdownModule,
  notes: NotesModule,
};

// ─── Loading Skeleton (Suspense fallback) ────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div
      className="flex flex-col items-center justify-center h-full min-h-[300px] gap-4"
      role="status"
      aria-label="Loading module"
    >
      <div className="animate-pulse flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary-light/50" />
        <div className="w-32 h-4 rounded-full bg-primary-light/30" />
        <div className="w-24 h-3 rounded-full bg-primary-light/20" />
      </div>
    </div>
  );
}

// ─── ModuleRouter Component ──────────────────────────────────────────────────

export function ModuleRouter() {
  const activeModule = useUIStore((state) => state.activeModule);
  const restoreLastActiveModule = useUIStore((state) => state.restoreLastActiveModule);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedModule, setDisplayedModule] = useState<ModuleId>(activeModule);
  const previousModuleRef = useRef<ModuleId>(activeModule);
  const isInitialMount = useRef(true);

  // Restore last active module on launch (Req 1.4)
  useEffect(() => {
    void restoreLastActiveModule();
  }, [restoreLastActiveModule]);

  // Handle module transitions with fade + scale animation
  useEffect(() => {
    if (isInitialMount.current) {
      // On initial mount, just display the module without animation
      isInitialMount.current = false;
      setDisplayedModule(activeModule);
      previousModuleRef.current = activeModule;
      return;
    }

    if (activeModule === previousModuleRef.current) {
      return;
    }

    // Start transition: fade out current
    setIsTransitioning(true);

    // After half the transition, swap the displayed module
    const swapTimeout = setTimeout(() => {
      setDisplayedModule(activeModule);
      previousModuleRef.current = activeModule;
    }, 100);

    // After full transition, complete
    const completeTimeout = setTimeout(() => {
      setIsTransitioning(false);
    }, 200);

    return () => {
      clearTimeout(swapTimeout);
      clearTimeout(completeTimeout);
    };
  }, [activeModule]);

  const ActiveModuleComponent = MODULE_COMPONENTS[displayedModule];
  const journalRevealed = useJournalLockStore((s) => s.revealed);

  // Selecting Journal from the nav (or anywhere) auto-reveals it behind the PIN
  // gate: initialize the lock store, then reveal() (which keeps it locked so the
  // gate asks for a PIN). When navigating away from Journal, re-lock + hide it
  // for privacy.
  useEffect(() => {
    if (activeModule !== 'journal') return;

    const lock = useJournalLockStore.getState();
    void lock.initialize().finally(() => {
      // Only reveal if the user is still on the journal module.
      if (useUIStore.getState().activeModule === 'journal') {
        useJournalLockStore.getState().reveal();
      }
    });

    return () => {
      // Left the journal — re-lock and hide it so it stays private.
      useJournalLockStore.getState().hide();
    };
  }, [activeModule]);

  // Transition styles: fade + scale (200ms), reduced to opacity-only for prefers-reduced-motion
  const transitionClasses = isTransitioning
    ? 'opacity-0 scale-[0.98]'
    : 'opacity-100 scale-100';

  // The Journal is reachable from the nav like any other module, but its
  // content stays gated behind a PIN. Selecting it reveals the lock gate (see
  // the effect above); until the store reveals it (a near-instant async
  // initialize), show the loading skeleton rather than a dead-end.
  const renderModule = () => {
    if (displayedModule === 'journal') {
      if (!journalRevealed) {
        return <LoadingSkeleton />;
      }
      return (
        <JournalLockGate>
          <ActiveModuleComponent />
        </JournalLockGate>
      );
    }
    return <ActiveModuleComponent />;
  };

  return (
    <div className="flex-1 h-full overflow-hidden" role="region" aria-label="Module content">
      <div
        className={[
          'h-full transition-[opacity,transform] duration-200 ease-in-out',
          'motion-reduce:transition-[opacity] motion-reduce:transform-none',
          transitionClasses,
        ].join(' ')}
      >
        <Suspense fallback={<LoadingSkeleton />}>
          {renderModule()}
        </Suspense>
      </div>
    </div>
  );
}

export default ModuleRouter;
